import type { JSONPatchOperation } from 'immutable-json-patch'
import type { StructuredValue } from './structured'
import { fieldLabel } from './labels'

export type FieldPath = readonly (string | number)[]
export type FieldKind = 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array'
export type StructuredField = { id: string; path: FieldPath; label: string; value: StructuredValue; kind: FieldKind }
export type FieldEdit = { field: StructuredField; value: StructuredValue }

export const pointerOf = (path: FieldPath) => path.length
  ? '/' + path.map((part) => String(part).replace(/~/g, '~0').replace(/\//g, '~1')).join('/') : ''
export const kindOf = (value: StructuredValue): FieldKind => {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value as Exclude<FieldKind, 'null' | 'array'>
}
export const containerOf = (value: StructuredValue) => value !== null && typeof value === 'object'

export const valueAt = (value: StructuredValue, path: FieldPath): StructuredValue => path.reduce<StructuredValue>((parent, key) => {
  if (!containerOf(parent) || !Object.prototype.hasOwnProperty.call(parent, key)) throw new Error('分组已不存在')
  return (parent as Record<string | number, StructuredValue>)[key]
}, value)

export const fieldsOf = (value: StructuredValue, path: FieldPath = []): StructuredField[] => {
  if (!containerOf(value)) return [{ id: pointerOf(path), label: '配置值', path, value, kind: kindOf(value) }]
  const entries = Array.isArray(value) ? value.map((item, index) => [index, item] as const) : Object.entries(value!)
  return entries.map(([key, item], index) => ({ id: pointerOf([...path, key]), path: [...path, key],
    label: fieldLabel(key, index), value: item, kind: kindOf(item) }))
}

export const EMPTY_VALUES: Record<FieldKind, () => StructuredValue> = {
  string: () => '', number: () => 0, boolean: () => false, null: () => null, object: () => ({}), array: () => [],
}
const forbidden = new Set(['__proto__', 'constructor', 'prototype'])

export const additionsAt = (root: StructuredValue, path: FieldPath, kind: FieldKind, name = ''): JSONPatchOperation[] => {
  const parent = valueAt(root, path)
  if (!containerOf(parent)) throw new Error('请选择分组或列表')
  if (Array.isArray(parent)) return [{ op: 'test', path: pointerOf(path), value: parent },
    { op: 'add', path: pointerOf([...path, '-']), value: EMPTY_VALUES[kind]() }]
  const key = name.trim()
  if (!key || forbidden.has(key)) throw new Error('请填写有效的字段名称')
  if (Object.prototype.hasOwnProperty.call(parent, key)) throw new Error('字段已存在')
  return [{ op: 'test', path: pointerOf(path), value: parent },
    { op: 'add', path: pointerOf([...path, key]), value: EMPTY_VALUES[kind]() }]
}
